/**
 * One click of "Ask Iris" (CU-869fkdhjv phase 3, plan § 2.1): the evidence, then the question.
 *
 * `DSB.setContext({ canvas_state })` first — sticky and merged per key (`widget.js:114-124`), so the whole object
 * replaces the previous evidence and `profile` is untouched — then `DSB.ask(question)`, which opens the panel, puts
 * the question in the user's bubble and sends it (`widget.js:744-750`).
 *
 * 🧭 1-B (Curro, 2026-10-27): `ask` writes over what the user had typed and `send()` empties the box in its
 * synchronous part (`widget.js:1041`, before the first `await`), so the draft is read before and written back right
 * after. The box is the chat's own textarea, `.dsb-panel .dsb-foot textarea` (`widget.js:688,694`; the widget has
 * other textareas). Not found → nothing restored (= option C). Remove when the brain team ships option A.
 *
 * Help mode (CU-869fmufdb): with `help` (the screen hint, `{ screen, tab, view }`), the evidence is cleared and the
 * hint rides on that turn only, as the widget's one-turn option `DSB.ask(text, { context })` (brain team,
 * 2026-10-28) — never through `setContext`, so the next typed question carries none of it. webback turns the hint
 * into the help pages; an older widget ignores the option (no help, never a leak).
 *
 * Feature-detected: an older widget without `setContext`, a handle not ready, or a turn in flight → nothing, and
 * `false`. Pure — the box query is injected — so `node --test` covers it (TL R4).
 */
export const DRAFT_BOX_SELECTOR = '.dsb-panel .dsb-foot textarea'

export const draftBox = () => (typeof document === 'undefined' ? null : document.querySelector(DRAFT_BOX_SELECTOR))

const readState = dsb => (typeof dsb.state === 'function' ? dsb.state() || {} : {})

export function askIris({ dsb, question, state, help, getDraftBox = draftBox }) {
	if (!dsb || typeof dsb.setContext !== 'function' || typeof dsb.ask !== 'function' || !question) return false
	const st = readState(dsb)
	if (st.ready === false) return false
	const busy = typeof dsb.isBusy === 'function' ? dsb.isBusy() : st.busy
	if (busy) return false

	const box = getDraftBox ? getDraftBox() : null
	const draft = box && typeof box.value === 'string' ? box.value : ''

	if (help) {
		dsb.setContext({ canvas_state: null })
		dsb.ask(question, { context: { screen_help: help } })
	} else {
		dsb.setContext({ canvas_state: state })
		dsb.ask(question)
	}

	if (box && draft) {
		box.value = draft
		if (typeof box.dispatchEvent === 'function' && typeof Event === 'function') box.dispatchEvent(new Event('input', { bubbles: true }))
	}
	return true
}

/** The evidence off: leaving the screen, logging out, or new data on it (plan § 2.3). */
export function clearAskContext(dsb) {
	if (!dsb || typeof dsb.setContext !== 'function') return false
	dsb.setContext({ canvas_state: null })
	return true
}
